import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Plus, Trash2, Play, Square } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { fetchFaq, fetchTestimonials } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function AdminSettings() {
    const [settings, setSettings] = useState(null);
    const [testimonials, setTestimonials] = useState([]);
    const [faq, setFaq] = useState([]);
    const [voiceModels, setVoiceModels] = useState({ tts_models: [], stt_models: [] });
    const [carriers, setCarriers] = useState([]);
    const [policyTypes, setPolicyTypes] = useState([]);
    const [previewing, setPreviewing] = useState(false);
    const previewAudioRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [s, t, f, models, car, pt] = await Promise.all([
                adminApi.settings(),
                fetchTestimonials(),
                fetchFaq(),
                adminApi.voiceModels().catch(() => ({ tts_models: [], stt_models: [] })),
                adminApi.listCarriersAll().catch(() => []),
                adminApi.listPolicyTypesAll().catch(() => []),
            ]);
            setSettings(s);
            setTestimonials(t);
            setFaq(f);
            setVoiceModels(models);
            setCarriers(car);
            setPolicyTypes(pt);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadAll(); }, []);

    if (loading || !settings) {
        return <Loader2 className="mx-auto animate-spin text-mm-navy" />;
    }

    const patch = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const patchNested = (group, k, v) =>
        setSettings((s) => ({ ...s, [group]: { ...(s[group] || {}), [k]: v } }));

    const saveSettings = async () => {
        setSaving(true);
        try {
            await adminApi.updateSettings(settings);
            toast.success("Saved.");
        } catch {
            toast.error("Couldn't save.");
        } finally {
            setSaving(false);
        }
    };

    const stopPreview = () => {
        if (previewAudioRef.current) {
            try { previewAudioRef.current.pause(); } catch { /* noop */ }
            previewAudioRef.current = null;
        }
        setPreviewing(false);
    };

    const playPreview = async () => {
        // Save any pending voice tweaks first so the preview reflects what's staged.
        try {
            setPreviewing(true);
            await adminApi.updateSettings({ voice_settings: settings.voice_settings || {} });
            const blob = await adminApi.voicePreview(
                "This is a short preview of the current Jarvis voice settings."
            );
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            previewAudioRef.current = audio;
            audio.onended = () => { setPreviewing(false); URL.revokeObjectURL(url); };
            audio.onerror = () => { setPreviewing(false); toast.error("Couldn't play preview."); };
            await audio.play();
        } catch (err) {
            setPreviewing(false);
            const msg = err?.response?.data?.detail || "Preview failed.";
            toast.error(typeof msg === "string" ? msg : "Preview failed.");
        }
    };

    const saveTestimonial = async (t, i) => {
        try {
            if (t.id?.startsWith("new-")) {
                const res = await adminApi.createTestimonial({
                    quote: t.quote, attribution: t.attribution, order: t.order, active: t.active
                });
                setTestimonials((arr) => arr.map((x, idx) => idx === i ? res : x));
            } else {
                await adminApi.updateTestimonial(t.id, t);
            }
            toast.success("Testimonial saved.");
        } catch { toast.error("Couldn't save testimonial."); }
    };
    const addTestimonial = () =>
        setTestimonials((arr) => [...arr, { id: `new-${Date.now()}`, quote: "", attribution: "", order: arr.length + 1, active: true }]);
    const deleteTestimonial = async (id) => {
        if (!window.confirm("Delete this testimonial?")) return;
        if (!id.startsWith("new-")) await adminApi.deleteTestimonial(id);
        setTestimonials((arr) => arr.filter((x) => x.id !== id));
    };

    const saveFaq = async (q, i) => {
        try {
            if (q.id?.startsWith("new-")) {
                const res = await adminApi.createFaq({
                    question: q.question, answer: q.answer, order: q.order, active: q.active
                });
                setFaq((arr) => arr.map((x, idx) => idx === i ? res : x));
            } else {
                await adminApi.updateFaq(q.id, q);
            }
            toast.success("FAQ saved.");
        } catch { toast.error("Couldn't save FAQ."); }
    };
    const addFaq = () =>
        setFaq((arr) => [...arr, { id: `new-${Date.now()}`, question: "", answer: "", order: arr.length + 1, active: true }]);
    const deleteFaq = async (id) => {
        if (!window.confirm("Delete this FAQ?")) return;
        if (!id.startsWith("new-")) await adminApi.deleteFaq(id);
        setFaq((arr) => arr.filter((x) => x.id !== id));
    };

    return (
        <div className="space-y-8" data-testid="admin-settings">
            <header className="flex items-center justify-between">
                <div>
                    <div className="mm-eyebrow">Settings</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Site configuration</h1>
                </div>
                <button onClick={saveSettings} disabled={saving} className="mm-btn-primary" data-testid="settings-save">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save site settings
                </button>
            </header>

            <Section title="Brand & identity">
                <Field k="brand_name" label="Brand name" value={settings.brand_name} onChange={patch} />
                <Field k="tagline" label="Tagline" value={settings.tagline} onChange={patch} />
                <Field k="hero_headline" label="Hero headline" value={settings.hero_headline} onChange={patch} />
                <FieldArea k="hero_subheadline" label="Hero subheadline" value={settings.hero_subheadline} onChange={patch} />
                <Field k="required_license" label="Required license" value={settings.required_license} onChange={patch} />
            </Section>

            <Section title="Contact">
                <Field k="contact_email" label="Contact email" value={settings.contact_email} onChange={patch} />
                <Field k="contact_phone" label="Contact phone" value={settings.contact_phone} onChange={patch} />
                <Field k="office_hours" label="Office hours" value={settings.office_hours} onChange={patch} />
            </Section>

            <Section
                title="Voice (Jarvis TTS + STT)"
                action={
                    previewing ? (
                        <button
                            onClick={stopPreview}
                            className="mm-btn-outline !py-1.5 text-xs"
                            data-testid="voice-preview-stop"
                        >
                            <Square size={12} /> Stop
                        </button>
                    ) : (
                        <button
                            onClick={playPreview}
                            className="mm-btn-outline !py-1.5 text-xs"
                            data-testid="voice-preview-play"
                        >
                            <Play size={12} /> Preview voice
                        </button>
                    )
                }
            >
                <div className="mm-card p-4 space-y-4" data-testid="voice-settings">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-mm-muted">
                                Provider
                            </Label>
                            <select
                                value={settings.voice_settings?.voice_provider || "none"}
                                onChange={(e) =>
                                    patchNested("voice_settings", "voice_provider", e.target.value)
                                }
                                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                                data-testid="voice-provider-select"
                            >
                                <option value="elevenlabs">ElevenLabs</option>
                                <option value="none">Disabled</option>
                            </select>
                        </div>
                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-mm-muted">
                                Voice ID
                            </Label>
                            <Input
                                value={settings.voice_settings?.voice_id || ""}
                                onChange={(e) =>
                                    patchNested("voice_settings", "voice_id", e.target.value)
                                }
                                placeholder="e.g. JBFqnCBsd6RMkjVDRZzb"
                                data-testid="voice-id-input"
                            />
                            <p className="mt-1 text-[11px] text-mm-muted">
                                From your ElevenLabs voice library.
                            </p>
                        </div>
                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-mm-muted">
                                TTS model
                            </Label>
                            <select
                                value={
                                    settings.voice_settings?.voice_model_id || "eleven_turbo_v2_5"
                                }
                                onChange={(e) =>
                                    patchNested("voice_settings", "voice_model_id", e.target.value)
                                }
                                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                                data-testid="voice-model-select"
                            >
                                {voiceModels.tts_models?.map((m) => (
                                    <option key={m.id} value={m.id}>{m.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-mm-muted">
                                STT model
                            </Label>
                            <select
                                value={settings.voice_settings?.stt_model_id || "scribe_v1"}
                                onChange={(e) =>
                                    patchNested("voice_settings", "stt_model_id", e.target.value)
                                }
                                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                                data-testid="voice-stt-select"
                            >
                                {voiceModels.stt_models?.map((m) => (
                                    <option key={m.id} value={m.id}>{m.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                        <Slider
                            label="Stability"
                            testid="voice-stability"
                            value={settings.voice_settings?.voice_stability ?? 0.55}
                            onChange={(v) => patchNested("voice_settings", "voice_stability", v)}
                        />
                        <Slider
                            label="Similarity boost"
                            testid="voice-similarity"
                            value={settings.voice_settings?.voice_similarity_boost ?? 0.8}
                            onChange={(v) =>
                                patchNested("voice_settings", "voice_similarity_boost", v)
                            }
                        />
                        <Slider
                            label="Style"
                            testid="voice-style"
                            value={settings.voice_settings?.voice_style ?? 0.35}
                            onChange={(v) => patchNested("voice_settings", "voice_style", v)}
                        />
                    </div>

                    <label className="flex items-center gap-2 text-sm text-mm-ink">
                        <input
                            type="checkbox"
                            checked={!!(settings.voice_settings?.voice_use_speaker_boost ?? true)}
                            onChange={(e) =>
                                patchNested(
                                    "voice_settings",
                                    "voice_use_speaker_boost",
                                    e.target.checked
                                )
                            }
                            data-testid="voice-speaker-boost"
                        />
                        Use speaker boost
                    </label>

                    <p className="text-[11px] text-mm-muted">
                        Changes save with the “Save site settings” button. Preview reflects the
                        currently staged values (it saves them first). Caching is cleared
                        automatically on save.
                    </p>
                </div>
            </Section>

            <Section title="Disclaimers (shown on footer + /privacy)">
                <FieldArea k="disclaimer_short" label="Short disclaimer" value={settings.disclaimer_short} onChange={patch} />
                <FieldArea k="disclaimer_long" label="Long disclaimer" value={settings.disclaimer_long} onChange={patch} />
                <Field k="we_do_not_sell_data" label="Data policy line" value={settings.we_do_not_sell_data} onChange={patch} />
                <Field k="ai_disclosure" label="AI disclosure" value={settings.ai_disclosure} onChange={patch} />
            </Section>

            <Section title="Earnings copy (recruiting page)">
                <FieldArea k="earnings_disclosure" label="Exact copy" value={settings.earnings_disclosure} onChange={patch} />
            </Section>

            <Section title="Identities">
                <div className="grid gap-4 md:grid-cols-2">
                    <div className="mm-card p-4 space-y-3">
                        <div className="mm-eyebrow">Brent</div>
                        <Row label="Name" value={settings.brent?.name} onChange={(v) => patchNested("brent", "name", v)} />
                        <Row label="Title" value={settings.brent?.title} onChange={(v) => patchNested("brent", "title", v)} />
                        <Row label="Photo URL" value={settings.brent?.photo} onChange={(v) => patchNested("brent", "photo", v)} />
                        <RowArea label="Bio" value={settings.brent?.bio} onChange={(v) => patchNested("brent", "bio", v)} />
                    </div>
                    <div className="mm-card p-4 space-y-3">
                        <div className="mm-eyebrow">Jeremy</div>
                        <Row label="Name" value={settings.jeremy?.name} onChange={(v) => patchNested("jeremy", "name", v)} />
                        <Row label="Title" value={settings.jeremy?.title} onChange={(v) => patchNested("jeremy", "title", v)} />
                        <RowArea label="Bio" value={settings.jeremy?.bio} onChange={(v) => patchNested("jeremy", "bio", v)} />
                    </div>
                </div>
            </Section>

            <Section title="Carriers & Policy Types">
                <div className="grid gap-4 md:grid-cols-2">
                    <CatalogManager
                        title="Carriers"
                        items={carriers}
                        setItems={setCarriers}
                        create={adminApi.createCarrier}
                        patch={adminApi.patchCarrier}
                        remove={adminApi.deleteCarrier}
                        testidPrefix="carrier"
                    />
                    <CatalogManager
                        title="Policy Types"
                        items={policyTypes}
                        setItems={setPolicyTypes}
                        create={adminApi.createPolicyType}
                        patch={adminApi.patchPolicyType}
                        remove={adminApi.deletePolicyType}
                        testidPrefix="policy-type"
                    />
                </div>
                <p className="text-[11px] text-mm-muted italic mt-2">
                    These values power the Carrier and Policy Type dropdowns on the Closed Deal form.
                    Inactive items stay on past policies but are hidden from new entries.
                </p>
            </Section>

            <Section title="Carrier Portals">
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid="carrier-portals-disclaimer">
                    Moving Mountains does not collect or process payments. These links route clients directly to each carrier&apos;s own portal.
                </div>
                <CarrierPortalsManager />
            </Section>

            <Section
                title="Testimonials"
                action={
                    <button onClick={addTestimonial} className="mm-btn-outline !py-1.5 text-xs" data-testid="add-testimonial">
                        <Plus size={12} /> Add
                    </button>
                }
            >
                <div className="space-y-3">
                    {testimonials.map((t, i) => (
                        <div key={t.id} className="mm-card p-4 space-y-2" data-testid={`testimonial-row-${t.id}`}>
                            <RowArea label="Quote" value={t.quote} onChange={(v) => {
                                const c = [...testimonials]; c[i] = { ...c[i], quote: v }; setTestimonials(c);
                            }} />
                            <div className="grid grid-cols-3 gap-2">
                                <Row label="Attribution" value={t.attribution} onChange={(v) => {
                                    const c = [...testimonials]; c[i] = { ...c[i], attribution: v }; setTestimonials(c);
                                }} />
                                <Row label="Order" value={t.order} onChange={(v) => {
                                    const c = [...testimonials]; c[i] = { ...c[i], order: Number(v) || 0 }; setTestimonials(c);
                                }} />
                                <label className="flex items-end gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!t.active}
                                        onChange={(e) => {
                                            const c = [...testimonials]; c[i] = { ...c[i], active: e.target.checked }; setTestimonials(c);
                                        }}
                                    />
                                    Active
                                </label>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => saveTestimonial(t, i)} className="mm-btn-primary !py-1.5 text-xs">Save</button>
                                <button onClick={() => deleteTestimonial(t.id)} className="mm-btn-ghost text-xs text-rose-700"><Trash2 size={12}/> Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            <Section
                title="FAQ"
                action={
                    <button onClick={addFaq} className="mm-btn-outline !py-1.5 text-xs" data-testid="add-faq">
                        <Plus size={12} /> Add
                    </button>
                }
            >
                <div className="space-y-3">
                    {faq.map((q, i) => (
                        <div key={q.id} className="mm-card p-4 space-y-2" data-testid={`faq-row-${q.id}`}>
                            <Row label="Question" value={q.question} onChange={(v) => {
                                const c = [...faq]; c[i] = { ...c[i], question: v }; setFaq(c);
                            }} />
                            <RowArea label="Answer" value={q.answer} onChange={(v) => {
                                const c = [...faq]; c[i] = { ...c[i], answer: v }; setFaq(c);
                            }} />
                            <div className="grid grid-cols-3 gap-2">
                                <Row label="Order" value={q.order} onChange={(v) => {
                                    const c = [...faq]; c[i] = { ...c[i], order: Number(v) || 0 }; setFaq(c);
                                }} />
                                <label className="flex items-end gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={!!q.active}
                                        onChange={(e) => {
                                            const c = [...faq]; c[i] = { ...c[i], active: e.target.checked }; setFaq(c);
                                        }}
                                    />
                                    Active
                                </label>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => saveFaq(q, i)} className="mm-btn-primary !py-1.5 text-xs">Save</button>
                                <button onClick={() => deleteFaq(q.id)} className="mm-btn-ghost text-xs text-rose-700"><Trash2 size={12}/> Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}

function Section({ title, action, children }) {
    return (
        <section className="space-y-3">
            <div className="flex items-center justify-between">
                <h2 className="mm-display text-lg text-mm-navy">{title}</h2>
                {action}
            </div>
            {children}
        </section>
    );
}

function Field({ k, label, value, onChange }) {
    return (
        <div>
            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-mm-muted">{label}</Label>
            <Input value={value || ""} onChange={(e) => onChange(k, e.target.value)} />
        </div>
    );
}
function FieldArea({ k, label, value, onChange }) {
    return (
        <div>
            <Label className="mb-1.5 block text-xs uppercase tracking-wider text-mm-muted">{label}</Label>
            <Textarea rows={3} value={value || ""} onChange={(e) => onChange(k, e.target.value)} />
        </div>
    );
}
function Row({ label, value, onChange }) {
    return (
        <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">{label}</Label>
            <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </div>
    );
}
function RowArea({ label, value, onChange }) {
    return (
        <div>
            <Label className="mb-1 block text-[10px] uppercase tracking-wider text-mm-muted">{label}</Label>
            <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </div>
    );
}

function Slider({ label, value, onChange, testid }) {
    const v = Number.isFinite(value) ? value : 0;
    return (
        <div>
            <div className="mb-1 flex items-center justify-between">
                <Label className="block text-xs uppercase tracking-wider text-mm-muted">
                    {label}
                </Label>
                <span className="font-mono text-xs text-mm-ink" data-testid={`${testid}-value`}>
                    {v.toFixed(2)}
                </span>
            </div>
            <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={v}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full accent-mm-navy"
                data-testid={testid}
            />
        </div>
    );
}


function CatalogManager({ title, items, setItems, create, patch, remove, testidPrefix }) {
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);

    const add = async () => {
        const name = draft.trim();
        if (!name) return;
        if (items.some((it) => it.name.toLowerCase() === name.toLowerCase())) {
            toast.error("Already exists.");
            return;
        }
        setBusy(true);
        try {
            const created = await create({ name, active: true });
            setItems((arr) => [...arr, created].sort((a, b) => (a.order || 0) - (b.order || 0)));
            setDraft("");
            toast.success(`${title.slice(0, -1)} added.`);
        } catch {
            toast.error("Couldn't create.");
        } finally {
            setBusy(false);
        }
    };

    const rename = async (id, name) => {
        const value = (name || "").trim();
        if (!value) return;
        try {
            const updated = await patch(id, { name: value });
            setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...updated } : it)));
        } catch {
            toast.error("Couldn't rename.");
        }
    };

    const toggleActive = async (id, active) => {
        try {
            const updated = await patch(id, { active });
            setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...updated } : it)));
        } catch {
            toast.error("Couldn't update.");
        }
    };

    const setOrder = async (id, order) => {
        const n = Number(order);
        if (!Number.isFinite(n)) return;
        try {
            const updated = await patch(id, { order: n });
            setItems((arr) =>
                arr
                    .map((it) => (it.id === id ? { ...it, ...updated } : it))
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
            );
        } catch {
            toast.error("Couldn't reorder.");
        }
    };

    const del = async (id) => {
        if (!window.confirm(`Delete this ${title.toLowerCase().slice(0, -1)}?`)) return;
        try {
            await remove(id);
            setItems((arr) => arr.filter((it) => it.id !== id));
            toast.success("Deleted.");
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    return (
        <div className="mm-card p-4 space-y-3" data-testid={`${testidPrefix}-catalog`}>
            <div className="flex items-center justify-between">
                <div className="mm-eyebrow">{title}</div>
                <span className="text-[10px] text-mm-muted">
                    {items.filter((i) => i.active).length} active · {items.length} total
                </span>
            </div>

            <div className="flex gap-2">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") add(); }}
                    placeholder={`Add ${title.toLowerCase().slice(0, -1)}…`}
                    data-testid={`${testidPrefix}-new-input`}
                />
                <button
                    onClick={add}
                    disabled={busy || !draft.trim()}
                    className="mm-btn-outline !py-1.5 text-xs whitespace-nowrap"
                    data-testid={`${testidPrefix}-add-btn`}
                >
                    <Plus size={12} /> Add
                </button>
            </div>

            <ul className="space-y-2">
                {items.length === 0 && (
                    <li className="text-[11px] text-mm-muted italic">None yet. Add your first above.</li>
                )}
                {items.map((it) => (
                    <li
                        key={it.id}
                        className="grid grid-cols-[1fr_64px_auto_auto] items-center gap-2 rounded-md border border-slate-100 bg-white px-2.5 py-1.5"
                        data-testid={`${testidPrefix}-row-${it.id}`}
                    >
                        <input
                            defaultValue={it.name}
                            onBlur={(e) => {
                                if (e.target.value.trim() && e.target.value.trim() !== it.name) {
                                    rename(it.id, e.target.value.trim());
                                }
                            }}
                            className="border-0 bg-transparent text-sm text-mm-ink focus:outline-none"
                            data-testid={`${testidPrefix}-name-${it.id}`}
                        />
                        <input
                            type="number"
                            defaultValue={it.order ?? 0}
                            onBlur={(e) => {
                                if (Number(e.target.value) !== it.order) {
                                    setOrder(it.id, e.target.value);
                                }
                            }}
                            className="w-16 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-center text-[11px]"
                            title="Display order"
                            data-testid={`${testidPrefix}-order-${it.id}`}
                        />
                        <label className="flex items-center gap-1 text-[11px] text-mm-muted">
                            <input
                                type="checkbox"
                                checked={!!it.active}
                                onChange={(e) => toggleActive(it.id, e.target.checked)}
                                data-testid={`${testidPrefix}-active-${it.id}`}
                            />
                            Active
                        </label>
                        <button
                            onClick={() => del(it.id)}
                            className="rounded p-1 text-rose-700 hover:bg-rose-50"
                            title="Delete"
                            data-testid={`${testidPrefix}-delete-${it.id}`}
                        >
                            <Trash2 size={12} />
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function CarrierPortalsManager() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState({}); // { carrier_id: { portal_url, customer_service_phone, notes } }
    const [saving, setSaving] = useState({}); // { carrier_id: true }

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminApi.carrierPortals();
            setRows(r);
            const map = {};
            r.forEach((row) => {
                map[row.carrier_id] = {
                    portal_url: row.portal_url || "",
                    customer_service_phone: row.customer_service_phone || "",
                    notes: row.notes || "",
                };
            });
            setDrafts(map);
        } catch {
            toast.error("Couldn't load carrier portals.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const patch = (id, k, v) =>
        setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [k]: v } }));

    const save = async (row) => {
        const d = drafts[row.carrier_id] || {};
        const url = (d.portal_url || "").trim();
        if (url && !url.startsWith("https://")) {
            toast.error("Portal URL must start with https://");
            return;
        }
        setSaving((s) => ({ ...s, [row.carrier_id]: true }));
        try {
            await adminApi.upsertCarrierPortal({
                carrier_id: row.carrier_id,
                portal_url: url || null,
                customer_service_phone: (d.customer_service_phone || "").trim() || null,
                notes: (d.notes || "").trim() || null,
            });
            toast.success(`${row.carrier_name} portal saved.`);
            await load();
        } catch (err) {
            const msg = err?.response?.data?.fields?.[0]?.message
                || err?.response?.data?.detail
                || "Couldn't save.";
            toast.error(msg);
        } finally {
            setSaving((s) => ({ ...s, [row.carrier_id]: false }));
        }
    };

    const clear = async (row) => {
        if (!row.id) return;
        if (!window.confirm(`Clear portal link for ${row.carrier_name}?`)) return;
        try {
            await adminApi.deleteCarrierPortal(row.id);
            toast.success(`${row.carrier_name} portal cleared.`);
            await load();
        } catch {
            toast.error("Couldn't clear.");
        }
    };

    if (loading) {
        return <Loader2 className="animate-spin text-mm-navy" />;
    }
    if (rows.length === 0) {
        return (
            <div className="rounded-lg border border-mm-line bg-white p-4 text-sm text-mm-muted">
                Add the official payment URL for each carrier. We will never collect payments — we only redirect clients to the carrier.
            </div>
        );
    }

    return (
        <div className="mm-card overflow-hidden p-0" data-testid="carrier-portals-list">
            <table className="w-full text-sm">
                <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                    <tr>
                        <th className="px-3 py-2.5 text-left font-medium">Carrier</th>
                        <th className="px-3 py-2.5 text-left font-medium">Portal URL (https://)</th>
                        <th className="px-3 py-2.5 text-left font-medium">Customer Service Phone</th>
                        <th className="px-3 py-2.5 text-left font-medium">Notes</th>
                        <th className="px-3 py-2.5 text-left font-medium">Last Updated</th>
                        <th className="px-3 py-2.5" />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const d = drafts[row.carrier_id] || {};
                        const dirty =
                            (d.portal_url || "") !== (row.portal_url || "") ||
                            (d.customer_service_phone || "") !== (row.customer_service_phone || "") ||
                            (d.notes || "") !== (row.notes || "");
                        return (
                            <tr
                                key={row.carrier_id}
                                className="border-t border-mm-line"
                                data-testid={`carrier-portal-row-${row.carrier_id}`}
                            >
                                <td className="px-3 py-2 font-medium text-mm-navy">
                                    {row.carrier_name}
                                    {!row.carrier_active && (
                                        <span className="ml-1 text-[9px] text-mm-muted">(inactive)</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    <Input
                                        value={d.portal_url || ""}
                                        placeholder="https://www.carrier.com/login"
                                        onChange={(e) => patch(row.carrier_id, "portal_url", e.target.value)}
                                        data-testid={`carrier-portal-url-${row.carrier_id}`}
                                        className="h-8 text-xs"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <Input
                                        value={d.customer_service_phone || ""}
                                        placeholder="+1-800-..."
                                        onChange={(e) => patch(row.carrier_id, "customer_service_phone", e.target.value)}
                                        data-testid={`carrier-portal-phone-${row.carrier_id}`}
                                        className="h-8 text-xs"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <Input
                                        value={d.notes || ""}
                                        placeholder="Optional"
                                        onChange={(e) => patch(row.carrier_id, "notes", e.target.value)}
                                        data-testid={`carrier-portal-notes-${row.carrier_id}`}
                                        className="h-8 text-xs"
                                    />
                                </td>
                                <td className="px-3 py-2 text-[10px] text-mm-muted whitespace-nowrap">
                                    {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : "—"}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => save(row)}
                                            disabled={!dirty || saving[row.carrier_id]}
                                            className="mm-btn-primary !py-1 !px-2 text-[11px] disabled:opacity-40"
                                            data-testid={`carrier-portal-save-${row.carrier_id}`}
                                        >
                                            {saving[row.carrier_id] ? "Saving…" : "Save"}
                                        </button>
                                        {row.id && (
                                            <button
                                                onClick={() => clear(row)}
                                                className="rounded px-1.5 py-1 text-rose-700 hover:bg-rose-50 text-[11px]"
                                                data-testid={`carrier-portal-clear-${row.carrier_id}`}
                                                title="Clear"
                                            >
                                                <Trash2 size={11} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


const POLICY_TYPES_FOR_RATES = [
    { value: "term", label: "Term" },
    { value: "whole_life", label: "Whole Life" },
    { value: "iul", label: "IUL" },
    { value: "final_expense", label: "Final Expense" },
    { value: "annuity", label: "Annuity" },
    { value: "health", label: "Health" },
    { value: "medicare", label: "Medicare" },
    { value: "dental_vision", label: "Dental/Vision" },
];

// CommissionRatesManager removed in Phase 9 course-correction (no UI consumer).


function InlineNumber({ value, onSave, integer = false, testid }) {
    const [v, setV] = useState(String(value ?? ""));
    useEffect(() => { setV(String(value ?? "")); }, [value]);
    return (
        <input
            type="number"
            value={v}
            step={integer ? "1" : "0.01"}
            onChange={(e) => setV(e.target.value)}
            onBlur={() => {
                if (v !== String(value) && v !== "") onSave(v);
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") { setV(String(value ?? "")); e.currentTarget.blur(); }
            }}
            className="w-16 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-right text-xs tabular-nums focus:border-mm-navy focus:outline-none"
            data-testid={testid}
        />
    );
}

